import React from "react";
import { ChangeEvent, FormEvent, Fragment, KeyboardEvent, MouseEvent } from "react";
import ReactModal from "react-modal";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import { apiDelete, apiGet, apiPost, V2, V1 } from "../../Api";
import { formatDateLocal, formatDateTimeLocal, formatName, formatList, transformJoin, truncateEllipse } from "../../Utilities";
import HasRight from "../HasRight";
import LinkTr from "../LinkTr";
import OverflowMenuItem from "../OverflowMenuItem";
import TabControl from "../TabControl";
import ToolbarMenuItem from "../ToolbarMenuItem";
import { ITitleContext } from "../ViewContainer";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import { DateTime, Interval } from "luxon";
import ViewToolbar from "../ViewToolbar";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../blocks.css";
import "../../form.css";
import "../../icomoon.css";
import "../../modal.css";
import "../../table.css";
import "../viewer.css";
import Checkbox from "../Checkbox";



interface IHealthFairViewState {
    fair: any | null | string;
    stats: any[];
    insuranceStats: any[];
    surveys: any[];
    filteredSurveys: any[];
    searchText: string;
    isEmailModalOpen: boolean;
    isModalOpen: boolean;
    isRollModalOpen: boolean;
    paperSurveyCount: number;
    paperSurveyCountM: number;
    prizeWinner?: any;
    prizeWinnerM?: any;
    prizeHasSameName: boolean;
    prizeHasSameNameM: boolean;
    drawFemale: boolean;
    drawMale: boolean;

    modalLoading?: boolean | string;

    emailList: any[];

    isDeletionModalOpen: boolean;
    deletionModalLoading: boolean | string;

    isPastTime: boolean;

    rollSelection?: any;
    onCallNotices: any[];

    [name: string]: any;
}

interface IHealthFairViewProps extends RouteComponentProps<any>, IAuthProps {
    id: string;
    viewTitleContext: ITitleContext;
}

class HealthFairView extends React.Component<IHealthFairViewProps, IHealthFairViewState> {
    private inputTimer?: number;
    private refreshTimer?: number;

    constructor(props: Readonly<IHealthFairViewProps>) {
        super(props);

        this.state = {
            fair: null,
            filteredSurveys: [],
            stats: [],
            insuranceStats: [],
            surveys: [],

            searchText: "",

            isEmailModalOpen: false,
            isModalOpen: false,
            isRollModalOpen: false,
            paperSurveyCount: 0,
            paperSurveyCountM: 0,

            prizeHasSameName: false,
            prizeHasSameNameM: false,
            drawFemale: true,
            drawMale: false,

            emailList: [],

            deletionModalLoading: false,
            isDeletionModalOpen: false,

            isPastTime: false,
            onCallNotices: []
        };

        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.doSearch = this.doSearch.bind(this);
        this.hideKeyboard = this.hideKeyboard.bind(this);
        this.showMapWindow = this.showMapWindow.bind(this);
        this.showPrizeSelector = this.showPrizeSelector.bind(this);
        this.closePrizeSelector = this.closePrizeSelector.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.selectWinner = this.selectWinner.bind(this);
        this.saveWinner = this.saveWinner.bind(this);
        this.toggleFair = this.toggleFair.bind(this);
        this.toggleFairDigital = this.toggleFairDigital.bind(this);
        this.showEmailList = this.showEmailList.bind(this);
        this.closeEmailList = this.closeEmailList.bind(this);
        this.deleteItem = this.deleteItem.bind(this);
        this.realDeleteItem = this.realDeleteItem.bind(this);
        this.closeDeletionModal = this.closeDeletionModal.bind(this);
        this.doRoll = this.doRoll.bind(this);
        this.toggleRollModal = this.toggleRollModal.bind(this);
        this.showEmailTemplate = this.showEmailTemplate.bind(this);
    }

    public async componentDidMount() {
        await this.loadFair();

        await this.refreshEntries();
        this.refreshTimer = window.setInterval(async () => await this.refreshEntries(), 10000);
    }

    public componentWillUnmount() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
    }

    public render() {
        if (!this.state.fair) {
            return (
                <div className="spinner-full-root">
                    <div className="spinner lds-dual-ring-big" />
                </div>
            );
        }

        if (typeof this.state.fair === "string") {
            return (
                <div className="viewer-root">
                    <span className="error-text">Failed to load health fair</span>
                </div>
            );
        }

        const totalSurveyCount = this.state.surveys ? this.state.surveys.length : 0;
        const totalAppts = this.state.surveys ? this.state.surveys.filter(a => a.hasAppointment).length : 0;
        const totalSurveyF = this.state.surveys ? this.state.surveys.filter(a => a.sex === "F").length : 0;
        const totalSurveyM = this.state.surveys ? this.state.surveys.filter(a => a.sex === "M").length : 0;
        const totalApptsF = this.state.surveys ? this.state.surveys.filter(a => a.sex === "F" && a.hasAppointment).length : 0;
        const totalApptsM = this.state.surveys ? this.state.surveys.filter(a => a.sex === "M" && a.hasAppointment).length : 0;
        let winnerF = { name: "Undecided", id: null };
        let winnerM = { name: "Undecided", id: null };
        if (this.state.fair.winnerF) {
            if (this.state.fair.winnerF < 10000) {
                winnerF = { name: `Paper #${(Number(this.state.fair.winnerF) - 1000) + 1}`, id: null };
            }
            else if (this.state.surveys && this.state.surveys.length > 0) {
                const survey = this.state.surveys.find(a => a.id === this.state.fair.winnerF);
                winnerF = { name: formatName(survey.name), id: this.state.fair.winnerF };
            }
        }
        if (this.state.fair.winnerM) {
            if (this.state.fair.winnerM < 10000) {
                winnerM = { name: `Paper #${(Number(this.state.fair.winnerM) - 1000) + 1}`, id: null };
            }
            else if (this.state.surveys && this.state.surveys.length > 0) {
                const survey = this.state.surveys.find(a => a.id === this.state.fair.winnerM);
                winnerM = { name: formatName(survey.name), id: this.state.fair.winnerM };
            }
        }

        return (
            <div className={"viewer-root" + (!!this.state.fair.isActive || !!this.state.fair.isDigitalActive ? " has-alert-bar" : "")}>
                <ViewToolbar>
                    {!this.props.authContext.serverType
                        ? <HasRight right={!this.state.isPastTime ? "fair:write" : "fair:write:admin"}>
                            <Link to={{ pathname: this.props.location.pathname + "/edit" }} className="icon-pencil" title="Edit fair..." />
                        </HasRight>
                        : null}
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="survey:create">
                            <Link to={this.props.location.pathname + "/survey"}><i className="icon-plus" /><span>Enter questionnaire...</span></Link>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem>
                            <button type="button" onClick={this.showPrizeSelector}><i className="icon-gift1" /><span>Select winner</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem>
                            <button type="button" onClick={this.toggleRollModal}><i className="icon-random" /><span>Dice Roll</span></button>
                        </ToolbarMenuItem>

                        <ToolbarMenuItem condition={!this.state.fair.isActive}>
                            <button type="button" onClick={this.toggleFair}><i className="icon-eye" /><span>Show on tablet</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem condition={!!this.state.fair.isActive}>
                            <button type="button" onClick={this.toggleFair}><i className="icon-eye-slash" /><span>Stop showing on tablet</span></button>
                        </ToolbarMenuItem>

                        <ToolbarMenuItem right="fair:d:act" condition={!this.props.authContext.serverType && !this.state.fair.isDigitalActive}>
                            <button type="button" onClick={this.toggleFairDigital}><i className="icon-globe" /><span>Activate online fair</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="fair:d:act" condition={!this.props.authContext.serverType && !!this.state.fair.isDigitalActive}>
                            <button type="button" onClick={this.toggleFairDigital}><i className="icon-globe" /><span>Deactivate online fair</span></button>
                        </ToolbarMenuItem>

                        <ToolbarMenuItem right="fair:read:temp">
                            <button type="button" onClick={this.showEmailTemplate}><i className="icon-file-text-o" /><span>Email Template...</span></button>
                        </ToolbarMenuItem>

                        <ToolbarMenuItem right="fair:read:emails">
                            <button type="button" onClick={this.showEmailList}><i className="icon-envelope" /><span>Email list</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem anyRights={["fair:delete", "fair:delete:own"]} condition={!this.props.authContext.serverType}>
                            <button type="button" onClick={this.deleteItem}><i className="icon-trash" /><span>Delete health fair</span></button>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem anyRights={["fair:p:s:read", "fair:p:r:read"]} condition={!this.props.authContext.serverType}>
                            <Link to={this.props.location.pathname + "/photos"}><i className="icon-file-image-o" /><span>Photos...</span></Link>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem anyRights={["fair:doc:read"]} condition={!this.props.authContext.serverType}>
                            <Link to={this.props.location.pathname + "/documents"}><i className="icon-file-pdf-o" /><span>Documents...</span></Link>
                        </ToolbarMenuItem>
                        <ToolbarMenuItem right="survey:read" condition={!this.props.authContext.serverType}>
                            <Link to={this.props.location.pathname + "/print-all"}><i className="icon-print" /><span>Batch print surveys...</span></Link>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <ReactModal
                    isOpen={this.state.isModalOpen} onRequestClose={this.closePrizeSelector}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Prize selector">

                    <form className="input-form" onSubmit={this.selectWinner}>
                        <section className="info-block">
                            <header>
                                <h3>Prize Selector</h3>
                            </header>
                            {
                                this.state.prizeWinner || this.state.prizeWinnerM
                                    ? <Fragment>
                                        {this.state.prizeWinner && this.state.prizeWinnerM
                                            ? <span>And the winners are...</span>
                                            : <span>And the winner is...</span>}

                                        <hr />
                                        {
                                            this.state.prizeWinner
                                                ? <Fragment>
                                                    <p style={{ marginTop: "0" }}><b>Female:</b></p>
                                                    <h2 style={{ marginTop: "0", fontWeight: "normal" }}>{`${this.state.prizeWinner.name}!`}</h2>
                                                    {!this.state.prizeWinner.isPaper ? <span className="small-text">{formatDateLocal(this.state.prizeWinner.birthDate)}</span> : ""}
                                                    {this.state.prizeHasSameName ? <Fragment><br /><span className="small-text">(Note: There is another digital questionnaire today with the same name)</span></Fragment> : ""}
                                                </Fragment>
                                                : null
                                        }
                                        {this.state.prizeWinner && this.state.prizeWinnerM ? <hr /> : null}
                                        {
                                            this.state.prizeWinnerM
                                                ? <Fragment>
                                                    <p style={{ marginTop: "0" }}><b>Male:</b></p>
                                                    <h2 style={{ marginTop: "0", fontWeight: "normal" }}>{`${this.state.prizeWinnerM.name}!`}</h2>
                                                    {!this.state.prizeWinnerM.isPaper ? <span className="small-text">{formatDateLocal(this.state.prizeWinnerM.birthDate)}</span> : ""}
                                                    {this.state.prizeHasSameNameM ? <Fragment><br /><span className="small-text">(Note: There is another digital questionnaire today with the same name)</span></Fragment> : ""}
                                                    <br />
                                                </Fragment>
                                                : null
                                        }
                                        {
                                            typeof this.state.modalLoading === "string"
                                                ? <div className="error-box">{this.state.modalLoading}</div>
                                                : this.state.modalLoading === true
                                                    ? <div className="spinner spinner-center lds-dual-ring" />
                                                    : ""
                                        }
                                        <div className="action-panel-dialog">
                                            <button type="button" className="button btn-light push-right" onClick={this.selectWinner} disabled={this.state.modalLoading === true}>Re-Roll</button>
                                            <button type="button" className="button" onClick={this.saveWinner} disabled={this.state.modalLoading === true}>Save</button>
                                        </div>
                                    </Fragment>
                                    : <Fragment>
                                        <section>
                                            <div className="input-form-group">
                                                <div className="input-form-group-check-inline">
                                                    <Checkbox fancy={true} id="frm-hf-select-female" name="drawFemale" value="1" checked={this.state.drawFemale} onChange={this.formInputChanged} autoComplete="off_please_chrome">Select a female winner</Checkbox>
                                                </div>
                                            </div>
                                        </section>
                                        <section>
                                            <div className="input-form-group">
                                                <label htmlFor="frm-hf-survey-paper" className="required">Number of female paper questionnaires</label>
                                                <input type="number" id="frm-hf-survey-paper" autoFocus={true} name="paperSurveyCount" min={0} value={this.state.paperSurveyCount} onChange={this.formInputChanged} placeholder="Number of paper surveys" autoComplete="off_please_chrome" />
                                            </div>
                                        </section>
                                        <section>
                                            <div className="input-form-group">
                                                <div className="input-form-group-check-inline">
                                                    <Checkbox fancy={true} id="frm-hf-select-male" name="drawMale" value="1" checked={this.state.drawMale} onChange={this.formInputChanged} autoComplete="off_please_chrome">Select a male winner</Checkbox>
                                                </div>
                                            </div>
                                        </section>
                                        <section>
                                            <div className="input-form-group">
                                                <label htmlFor="frm-hf-survey-paper-male" className="required">Number of male paper questionnaires</label>
                                                <input type="number" id="frm-hf-survey-paper-male" name="paperSurveyCountM" min={0} value={this.state.paperSurveyCountM} onChange={this.formInputChanged} placeholder="Number of paper surveys" autoComplete="off_please_chrome" />
                                            </div>
                                        </section>
                                        <br />
                                        <div className="action-panel-dialog">
                                            <button type="button" className="button btn-light push-right" onClick={this.closePrizeSelector}>Cancel</button>
                                            <button type="submit" className="button" disabled={!this.state.drawFemale && !this.state.drawMale}>{this.state.drawMale && this.state.drawFemale ? "Select Winners" : "Select Winner"}</button>
                                        </div>
                                    </Fragment>
                            }
                        </section>
                    </form>

                </ReactModal>
                <ReactModal
                    isOpen={this.state.isRollModalOpen} onRequestClose={this.toggleRollModal}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal-roll"
                    bodyOpenClassName="modal-roll-open"
                    contentLabel="Dice Roll">

                    <section className="info-block">
                        <header>
                            <h3>Dice Roll</h3>
                        </header>

                        <p>Press <b>Roll</b> to select a random person from the submitted surveys.</p>
                        {
                            this.state.rollSelection
                                ? <p>
                                    <b>{this.state.rollSelection.name}</b><br />
                                    <span className="small-text">Born {formatDateLocal(this.state.rollSelection.birthDate)}</span>
                                </p>
                                : null
                        }

                        <div className="action-panel-dialog">
                            <button type="button" className="button btn-light push-right" onClick={this.toggleRollModal}>Close</button>
                            <button type="button" className="button" onClick={this.doRoll}>Roll</button>
                        </div>
                    </section>
                </ReactModal>
                <ReactModal
                    isOpen={this.state.isEmailModalOpen} onRequestClose={this.closeEmailList}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal-email-list"
                    bodyOpenClassName="modal-email-list-open"
                    contentLabel="Email List">

                    <form className="input-form">
                        <section className="info-block">
                            <header>
                                <h3>Email List</h3>
                            </header>

                            {
                                this.state.modalLoading
                                    ? <div className="spinner spinner-center lds-dual-ring" />
                                    : <Fragment>
                                        <h4>People that didn't schedule appointments</h4>
                                        <textarea readOnly={true} className="input-textarea-large" value={this.state.emailList.filter(a => !a.hasAppointment && a.email !== "none").map(a => a.email).join(",\r\n")} />

                                        <h4>People that scheduled appointments</h4>
                                        <textarea readOnly={true} value={this.state.emailList.filter(a => a.hasAppointment).map(a => a.email).join(",\r\n")} />
                                    </Fragment>
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button className="button push-right" onClick={this.closeEmailList}>Ok</button>
                            </div>
                        </section>
                    </form>

                </ReactModal>
                <ReactModal
                    isOpen={this.state.isDeletionModalOpen} onRequestClose={this.closeDeletionModal}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-deletion-portal"
                    bodyOpenClassName="modal-deletion-open"
                    contentLabel="Confirm Delete">
                    <form className="input-form" onSubmit={this.realDeleteItem}>
                        <section className="info-block">
                            <header>
                                <h3>Confirm Delete</h3>
                            </header>

                            <span>Are you sure you want to delete this health fair?</span>
                            <p>{this.state.fair.name}<br /><small>{formatDateTimeLocal(this.state.fair.scheduledDate)}</small></p>
                            {
                                this.state.deletionModalLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : (typeof this.state.deletionModalLoading) === "string"
                                        ? <span className="error-text">{this.state.deletionModalLoading}</span>
                                        : ""
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button type="submit" className="button red push-right">Yes, Delete</button>
                                <button type="button" className="button" onClick={this.closeDeletionModal}>No</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                {
                    !!this.state.fair.isActive
                        ? <div className="notification-bar green">
                            This fair is being shown on the tablets
                        </div>
                        : ""
                }
                {
                    !!this.state.fair.isDigitalActive
                        ? <div className="notification-bar bar-text-small-mobile green">
                            <span>This fair is available at&nbsp;</span><a href={`https://survey.howardhf.com/${this.state.fair.idCode}`} target="_blank" rel="noopener noreferrer">https://survey.howardhf.com/{this.state.fair.idCode}</a>
                        </div>
                        : ""
                }
                {this.state.onCallNotices.length > 0
                    ? this.state.onCallNotices.map(a => <div key={a.id} className="orange-box slim">{a.text}</div>)
                    : null}
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Health Fair Info</h3>
                        </header>
                        <dl className="info-list">
                            <dt>Location</dt>
                            <dd>{this.state.fair.name}</dd>
                            <dt>Address</dt>
                            <dd>
                                <a href={"https://maps.google.com/maps?daddr=" + encodeURIComponent(`${this.state.fair.address.street} ${this.state.fair.address.city}, ${this.state.fair.address.state}`)} target="_blank" rel="noopener noreferrer" onClick={this.showMapWindow}>
                                    {`${this.state.fair.address.street}`}<br />
                                    {`${this.state.fair.address.city}, ${this.state.fair.address.state}`}
                                </a>
                            </dd>
                            <dt>Date & Time</dt>
                            <dd>{formatDateTimeLocal(this.state.fair.scheduledDate)}</dd>
                            <dt>Assigned to</dt>
                            <dd>{this.state.fair.members.map((m: any) => formatName(m.name)).join(", ")}</dd>
                        </dl>
                    </section>
                    {
                        this.state.fair.contact ?
                            <section className="info-block">
                                <header>
                                    <h3>Company Info</h3>
                                </header>
                                <dl className="info-list">
                                    <dt>Contact Name</dt>
                                    <dd>{this.state.fair.contact.name ? this.state.fair.contact.name : "N/A"}</dd>
                                    <dt>Contact Phone number</dt>
                                    <dd>{this.state.fair.contact.phone ? <a href={`tel:${this.state.fair.contact.phone}`}>{this.formatPhone(this.state.fair.contact.phone)}</a> : "N/A"}</dd>
                                    <dt>Contact Email</dt>
                                    <dd>{(this.state.fair.contact.email ? (<a href={`mailto:${this.state.fair.contact.email}`}>{this.state.fair.contact.email}</a>) : this.state.fair.contact.email) || "N/A"}</dd>
                                    <dt>Fair Notes</dt>
                                    <dd>{this.state.fair.notes ? this.state.fair.notes.split(/[\n\r]/g).map((s: string, i: number) => (<Fragment key={i}>{s}<br /></Fragment>)) : "N/A"}</dd>
                                </dl>
                            </section>
                            : ""
                    }
                    <section className="info-block">
                        <header>
                            <h3>Stats</h3>
                        </header>
                        <dl className="info-list">
                            <dt>Expected head count</dt>
                            <dd>{this.state.fair.estimatedHeadCount}</dd>

                            <dt>Submitted questionnaires</dt>
                            <dd>{totalSurveyCount} <small>({(totalSurveyCount / this.state.fair.estimatedHeadCount * 100).toFixed(0)} %)</small></dd>
                            <dd><small>F: {totalSurveyF} ({totalSurveyF > 0 ? (totalSurveyF / totalSurveyCount * 100).toFixed(0) : 0} %), M: {totalSurveyM} ({totalSurveyM > 0 ? (totalSurveyM / totalSurveyCount * 100).toFixed(0) : 0} %)</small></dd>
                            {this.state.insuranceStats?.length > 0
                                ? <dd><small>{transformJoin(this.state.insuranceStats, a => `${a.provider}: ${a.count}`, " | ")}</small></dd>
                                : null
                            }

                            <dt>Appointments scheduled</dt>
                            <dd>{totalAppts} <small>({totalSurveyCount > 0 ? (totalAppts / totalSurveyCount * 100).toFixed(0) : 0} %)</small></dd>
                            <dd><small>F: {totalApptsF} ({totalApptsF ? (totalApptsF / totalAppts * 100).toFixed(0) : 0} %), M: {totalApptsM} ({totalApptsM > 0 ? (totalApptsM / totalAppts * 100).toFixed(0) : 0} %)</small></dd>

                            <dt>Winner(s)</dt>
                            {!winnerF.id && !winnerM.id
                                ? <dd>{winnerF.name}</dd>
                                : <Fragment>
                                    <dd>Female: {winnerF.name}</dd>
                                    <dd>Male: {winnerM.name}</dd>
                                </Fragment>
                            }
                        </dl>
                    </section>
                </div>
                &nbsp;
                <TabControl headers={["Questionnaires", "Stats"]} className="large-tabs">
                    <HasRight right="survey:read">
                        <div className="filter-control-box">
                            <input type="search" className="fill-space" placeholder="Search name" onChange={this.handleSearchChange} onKeyPress={this.hideKeyboard} />
                        </div>
                        <table className="table-vertical fill-space">
                            <thead>
                                <tr>
                                    <th className="col-30">First Name</th>
                                    <th className="col-30">Last Name</th>
                                    <th className="col-30">Date of Birth</th>
                                    <th className="col-smallest nowrap">Insurance</th>
                                    <th className="col-smallest"><i className="icon-gift1" title="Winner" /></th>
                                    <th className="col-smallest"><i className="icon-question" title="Answered 'Yes' to at least one question" /></th>
                                    <th className="col-smallest"><i className="icon-phone" title="Can contact?" /></th>
                                    <th className="col-smallest"><i className="icon-calendar" title="Appointment" /></th>
                                    <th className="col-smallest"><i className="icon-tablet" title="Origin device" /></th>
                                    <th className="col-smallest" />
                                    <th className="col-smallest print-hide" />
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr><th colSpan={10}>Questionnaires</th></tr>
                            </thead>
                            <tbody>
                                {this.renderTableBody(this.state.filteredSurveys)}
                            </tbody>
                        </table>
                    </HasRight>
                    <Fragment>
                        <table className="stats-table table-vertical wrap fill-space">
                            <thead>
                                <tr><th colSpan={10}>Question Stats</th></tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr><th colSpan={10}>Question Stats</th></tr>
                            </thead>
                            <tbody>
                                {
                                    this.state.stats.map(a => (
                                        <tr key={a.key}>
                                            <td>
                                                <div className="progress" style={{ width: `${(a.count / this.state.surveys.length) * 100}%` }} /><span>{a.text}</span>
                                                <span className="stats-percent">{`${((a.count / this.state.surveys.length) * 100).toFixed(0)}%`}</span>
                                            </td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </Fragment>
                </TabControl>
            </div>
        );
    }

    private renderTableBody(data: any[] | null) {
        if (!data) {
            return (
                <tr>
                    <td colSpan={10}>
                        <div className="spinner spinner-center lds-dual-ring" />
                    </td>
                </tr>
            );
        }
        else if (data.length === 0) {
            return (
                <tr>
                    <td colSpan={10}>
                        <strong className="text-center">No questionnaires found</strong>
                    </td>
                </tr>
            );
        }
        else {
            return data.map(r => (
                <LinkTr key={r.id} className={`clickable ${this.statusToClass(r.contactInfo?.status)}`} to={"/surveys/" + r.id}>
                    <td>{r.name.first}</td>
                    <td>{r.name.last}</td>
                    <td data-caption="Birthdate: ">{formatDateLocal(r.birthdate)}</td>
                    <td data-caption="Insurance: " title={r.insurance?.provider || ""}>{truncateEllipse(r.insurance?.provider || "",15)}</td>
                    <td className="col-smallest col-center">{(this.state.fair.winnerF === r.id || this.state.fair.winnerM === r.id) ? <i className="icon-gift1" title="Winner" /> : ""}</td>
                    <td className="col-smallest col-center">{(r.hasAnsweredQuestions) ? <i className="icon-question" title="Answered 'Yes' to at least one question" /> : ""}</td>
                    <td className="col-smallest col-center">{r.contactAllowed ? <i className="icon-phone" title="Can contact" /> : ""}</td>
                    <td className="col-smallest col-center">{r.hasAppointment ? <i className="icon-calendar-check-o" title="Has appointment scheduled" /> : ""}</td>
                    <td className="col-smallest col-center">{this.originToIcon(r.originDevice)}</td>
                    <td className="col-smallest col-center"><small>{r.sex}</small></td>
                    <td className="col-smallest print-hide">
                        <Link className="arrow arrow-box arrow-right" to={"/surveys/" + r.id} />
                    </td>
                </LinkTr>
            ));
        }
    }

    private handleSearchChange(e: ChangeEvent<HTMLInputElement>) {
        const v = e.target.value;
        this.setState({
            searchText: v,
        });

        clearTimeout(this.inputTimer);
        this.inputTimer = window.setTimeout(() => this.doSearch(v), 500);
    }

    private doSearch(value: string) {
        if (!this.state.surveys) {
            return;
        }

        if (!value) {
            this.setState(s => {
                return {
                    filteredSurveys: s.surveys
                }
            });
            return;
        }

        value = value.toLowerCase();

        const filter = (v: any) => (v.name.first as string).toLowerCase().includes(value) || (v.name.last as string).toLowerCase().includes(value);

        this.setState({
            filteredSurveys: this.state.surveys.filter(filter)
        });
    }

    private hideKeyboard(e: KeyboardEvent<HTMLInputElement>) {
        // NOTE: This is primarily for mobile clients to hide the virtual keyboard
        // It does have the side effect of removing focus from the search box when using
        // a hardware keyboard, which is not what the user is expecting.
        // However, the search box works without pressing enter, so it should be fine
        if (e.key === "Enter") {
            e.currentTarget.blur();
        }
    }

    private formatPhone(phone: string) {
        if (!phone) {
            return "";
        }

        phone = "1" + phone;
        const match = phone.match(/[+]?(\d{1,3})?(\d{3})(\d{3})(\d{4})$/);
        if (!match) {
            return phone;
        }

        return `(${match[2]}) ${match[3]}-${match[4]}`;
    }

    private async refreshEntries() {
        try {
            const include = !this.props.authContext.serverType && this.props.authContext.hasRight("survey:contact")
                ? "include=extended"
                : "";

            const [json, stats] = await Promise.all([
                apiGet<any[]>(`fairs/${this.props.match.params.id}/surveys/summary?${include}`),
                V2.get<any>(`fairs/${this.props.match.params.id}/stats`),
            ]);

            let answerStats: any[];
            let insuranceStats: any[] = [];
            if (Array.isArray(stats)) {
                // Previous API response directly returns an array
                answerStats = stats;
            }
            else {
                answerStats = stats.answers;
                insuranceStats = stats.insurance;
            }

            answerStats.sort((a, b) => b.count - a.count);
            this.setState({
                filteredSurveys: json,
                surveys: json,

                stats: answerStats,
                insuranceStats: insuranceStats
            });

            if (this.state.searchText) {
                this.doSearch(this.state.searchText);
            }
        }
        catch (e) {
            this.setState({
                filteredSurveys: [],
                surveys: []
            });
        }
    }

    private showMapWindow(e: MouseEvent<HTMLAnchorElement>) {
        e.preventDefault();

        window.open(e.currentTarget.href, undefined, "resizable,scrollbars,status");
    }

    private showPrizeSelector() {
        this.setState({
            isModalOpen: true,
            prizeWinner: null,
            prizeWinnerM: null,
        });
    }

    private closePrizeSelector() {
        this.setState({
            isModalOpen: false
        });
    }

    private async saveWinner() {
        try {
            this.setState({
                modalLoading: true
            });

            const data: any = {};
            if (this.state.prizeWinner) {
                data.female = this.state.prizeWinner;
            }
            if (this.state.prizeWinnerM) {
                data.male = this.state.prizeWinnerM;
            }

            // Save selected winner
            await V2.put(`fairs/${this.state.fair.id}/winner`, data);

            this.setState(s => {
                if (s.prizeWinner) {
                    s.fair.winnerF = s.prizeWinner.id;
                }
                if (s.prizeWinnerM) {
                    s.fair.winnerM = s.prizeWinnerM.id;
                }

                return {
                    fair: s.fair,
                    modalLoading: false,
                };
            });
            this.closePrizeSelector();
        }
        catch (e) {
            this.setState({
                modalLoading: e.message
            });
        }
    }

    private selectWinner(e: FormEvent) {
        e.preventDefault();

        if (!this.state.surveys || this.state.surveys.length === 0) {
            return;
        }

        const digitalSurveysF = this.state.surveys.filter(a => a.sex === "F");
        const digitalSurveysM = this.state.surveys.filter(a => a.sex === "M");

        const winnerF = this.state.drawFemale ? this.selectWinnerFromPool(digitalSurveysF, this.state.paperSurveyCount) : undefined;
        const winnerM = this.state.drawMale ? this.selectWinnerFromPool(digitalSurveysM, this.state.paperSurveyCountM) : undefined;

        const newState: Partial<IHealthFairViewState> = {};
        if (winnerF) {
            newState.prizeWinner = winnerF;
        }
        if (winnerM) {
            newState.prizeWinnerM = winnerM;
        }

        this.setState(newState);
    }

    private selectWinnerFromPool(digitalSurveys: any[], totalPaper: number) {
        const digitalPool = digitalSurveys.length;
        const totalPool = digitalPool + totalPaper;

        if (totalPool === 0) {
            // Nothing to select
            return;
        }

        const winner = Math.round(Math.random() * (totalPool - 1));

        if (winner >= digitalPool) {
            // Paper survey won
            const paperIndex = winner - digitalPool;
            return {
                birthDate: undefined,
                id: 1000 + paperIndex,
                isPaper: true,
                name: `Paper #${paperIndex + 1}`,
                hasSameName: false,
            };
        }
        else {
            // Digital survey won
            const survey = digitalSurveys[winner];
            const hasSameName = digitalSurveys.filter(a => a.name.first.toLowerCase() === survey.name.first.toLowerCase() && a.name.last.toLowerCase() === survey.name.last.toLowerCase()).length > 1;

            return {
                birthDate: survey.birthdate,
                isPaper: false,
                id: survey.id,
                name: formatName(survey.name),
                hasSameName
            };
        }
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? e.currentTarget.checked : e.currentTarget.value;

        if (e.currentTarget.type === "number" && typeof value !== "boolean") {
            this.setState({
                [name]: parseInt(value, 10)
            });
        }
        else {
            this.setState({
                [name]: value
            });
        }
    }

    private async loadFair() {
        this.setState({
            fair: undefined
        });

        const json = await apiGet<any>(`fairs/${this.props.match.params.id}`);

        this.props.viewTitleContext.changeTitle(`Health Fair - ${json.name}`, "window");
        this.props.viewTitleContext.changeTitle(json.name, "page");

        const fairTimestamp = DateTime.fromISO(json.scheduledDate);
        const [onCallStatusTelemarketer, onCallStatusSurgicalConsult] = await Promise.all([
            V2.get<any[]>(`schedule/coverage/status/${WellKnownGroups.PatientCareRep}/${fairTimestamp.toISODate()}`),
            V2.get<any[]>(`schedule/coverage/status/${WellKnownGroups.SurgicalConsultant}/${fairTimestamp.toISODate()}`)
        ]);

        // Convert on call status time ranges to a datetime interval
        for (const status of onCallStatusTelemarketer) {
            status.range = Interval.fromDateTimes(
                DateTime.fromISO(status.startTime).set({ year: fairTimestamp.year, month: fairTimestamp.month, day: fairTimestamp.day }),
                DateTime.fromISO(status.endTime).set({ year: fairTimestamp.year, month: fairTimestamp.month, day: fairTimestamp.day }),
            );
        }
        for (const status of onCallStatusSurgicalConsult) {
            status.range = Interval.fromDateTimes(
                DateTime.fromISO(status.startTime).set({ year: fairTimestamp.year, month: fairTimestamp.month, day: fairTimestamp.day }),
                DateTime.fromISO(status.endTime).set({ year: fairTimestamp.year, month: fairTimestamp.month, day: fairTimestamp.day }),
            );
        }

        // Notify if no one is on call or not on call during the time the fair is on
        let onCallNotices = [];
        if (onCallStatusTelemarketer.length === 0 || !onCallStatusTelemarketer.some(a => a.range.contains(fairTimestamp))) {
            onCallNotices.push("patient care rep.");
        }
        if (onCallStatusSurgicalConsult.length === 0 || !onCallStatusSurgicalConsult.some(a => a.range.contains(fairTimestamp))) {
            onCallNotices.push("surgical consultant");
        }

        if (onCallNotices.length > 0) {
            onCallNotices = [{
                id: "no-on-call",
                text: `No ${formatList(onCallNotices, undefined, "or")} ${onCallNotices.length === 1 ? "is" : "are"} on call during this fair`
            }];
        }

        this.setState({
            fair: json,
            isPastTime: DateTime.fromISO(json.scheduledDate) < DateTime.local(),
            onCallNotices
        });
    }

    private async toggleFair() {
        try {
            const newState = { active: !this.state.fair.isActive };
            await apiPost(`fairs/${this.state.fair.id}/toggle`, newState);

            // Reload fair
            this.loadFair();
        }
        catch (e) {
            this.setState({
                fair: e.message
            });
        }
    }

    private async toggleFairDigital() {
        try {
            const newState = { active: !this.state.fair.isDigitalActive };
            await V1.put(`fairs/${this.state.fair.id}/digital-status`, newState);

            // Reload fair
            this.loadFair();
        }
        catch (e) {
            this.setState({
                fair: e.message
            });
        }
    }

    private async showEmailList() {
        this.setState({
            isEmailModalOpen: true,
            modalLoading: true,
        });

        try {
            const data = await apiGet<any[]>(`fairs/${this.state.fair.id}/surveys/summary?include=email`);

            this.setState({
                emailList: data,
                modalLoading: false,
            });
        }
        catch (e) {
            this.setState({
                modalLoading: e
            });
        }
    }

    private closeEmailList() {
        this.setState({
            isEmailModalOpen: false
        });
    }

    private deleteItem() {
        this.setState({
            isDeletionModalOpen: true
        });
    }

    private closeDeletionModal() {
        this.setState({
            deletionModalLoading: false,
            isDeletionModalOpen: false
        });
    }

    private async realDeleteItem(e?: FormEvent) {
        if (e) {
            e.preventDefault();
        }

        try {
            this.setState({
                deletionModalLoading: true
            });

            await apiDelete(`fairs/${this.state.fair.id}`);

            this.setState({
                deletionModalLoading: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                deletionModalLoading: e.message || e
            });
        }
    }

    private toggleRollModal() {
        let { isRollModalOpen, rollSelection } = this.state;

        isRollModalOpen = !isRollModalOpen;
        if (!isRollModalOpen) {
            rollSelection = undefined;
        }

        this.setState({
            isRollModalOpen,
            rollSelection
        });
    }

    private doRoll() {
        const selected = this.selectWinnerFromPool(this.state.surveys, 0);

        this.setState({
            rollSelection: selected
        });
    }

    private statusToClass(status?: number) {
        switch (status) {
            case 7: return "row-orange";
            case 6: return "row-purple";
            case 5: return "row-blue";
            case 4: return "row-gray-dark";
            case 3: return "row-green";
            case 2: return "row-yellow";
            case 1: return "row-red";
            default: return "";
        }
    }

    private originToIcon(origin: number) {
        switch (origin) {
            case 0: return <i className="icon-blank" />
            case 1: return <i className="icon-tablet" title="via HF Tablet" />
            case 2: return <i className="icon-mobile" title="via Smartphone or computer" />
            case 3: return <i className="icon-globe" title="via Online survey" />
            default: return null;
        }
    }

    private async showEmailTemplate() {
        const w = 600;
        const h = 800;
        const y = window.top!.outerHeight / 2 + window.top!.screenY - (h / 2);
        const x = window.top!.outerWidth / 2 + window.top!.screenX - (w / 2);

        const wnd = window.open("about:blank", "email_template", `toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes,width=${w},height=${h},top=${y},left=${x}`);
        if (!wnd) {
            return;
        }

        // wnd.document.documentElement.innerHTML = "<h1>Loading, please wait...</h1>";

        try {
            const result = await V2.get<any>(`fairs/${this.state.fair.id}/template`);

            wnd.document.documentElement.innerHTML = result.result;
        }
        catch (e) {
            wnd.document.documentElement.innerHTML = `<h1>Failed to load</h1><p>${e.message || "Could not load template"}</p>`;
        }
    }
}

export default withTitle("Health Fair", withAuth(HealthFairView));
